import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Channel, ChatMessage } from '../features/chat/chat.types';
import * as channelService from '../services/channelService';

export const BUILT_IN_CHANNELS: Channel[] = [];

interface ChannelMessages {
  [channelGuid: string]: ChatMessage[];
}

interface ChannelContextValue {
  channels: Channel[];
  hasChannels: boolean;
  loading: boolean;
  activeChannelGuid: string | null;
  getChannel: (guid: string) => Channel | undefined;
  getChannelById: (id: string) => Channel | undefined;
  getChannelMessages: (channelGuid: string) => ChatMessage[];
  addMessage: (channelGuid: string, message: ChatMessage) => void;
  createChannel: (data: Omit<Channel, 'id' | 'guid' | 'createdAt'>) => Promise<Channel>;
  updateChannel: (guid: string, updates: Partial<Channel>) => Promise<Channel | null>;
  deleteChannel: (guid: string) => Promise<boolean>;
  setActiveChannel: (guid: string | null) => void;
  addAmpToChannel: (channelGuid: string, ampId: string) => Promise<Channel | null>;
  removeAmpFromChannel: (channelGuid: string, ampId: string) => Promise<Channel | null>;
  getChannelsForAmp: (ampId: string) => Channel[];
  refreshChannels: () => Promise<void>;
}

const ChannelContext = createContext<ChannelContextValue | null>(null);

export const useChannels = (): ChannelContextValue => {
  const ctx = useContext(ChannelContext);
  if (!ctx) throw new Error('useChannels must be used within ChannelProvider');
  return ctx;
};

const getDefaultChannelMessages = (channel: Channel | undefined): ChatMessage[] => {
  const channelName = channel?.name || 'Channel';
  return [
    {
      id: `${channel?.guid || 'default'}-welcome`,
      role: 'assistant',
      content: `Welcome to **#${channelName}**! ${channel?.description ? channel.description + ' ' : ''}I'm your AI assistant specialized for this channel. How can I help you today?`,
      timestamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    },
  ];
};

export const ChannelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelMessages, setChannelMessages] = useState<ChannelMessages>({});
  const [activeChannelGuid, setActiveChannelGuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Prevent duplicate refresh calls
  const refreshingRef = useRef(false);

  const refreshChannels = useCallback(async () => {
    // Prevent duplicate calls
    if (refreshingRef.current) {
      return;
    }

    refreshingRef.current = true;
    setLoading(true);
    setChannels([]);
    try {
      const loaded = await channelService.getChannels();
      setChannels(loaded);
    } catch (err) {
      console.error('Failed to load channels:', err);
      setChannels([]);
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  }, []);

  // Workspaces list is currently hidden from the UI (see SidebarLeft) — skip the
  // automatic GET /api/ChannelManagement/list fetch on mount/auth-refresh.
  useEffect(() => {
    setLoading(false);
  }, []);

  const hasChannels = useMemo(() => channels.length > 0, [channels]);

  const getChannel = useCallback((guid: string): Channel | undefined => {
    return channels.find(c => c.guid === guid);
  }, [channels]);

  const getChannelById = useCallback((id: string): Channel | undefined => {
    return channels.find(c => c.id === id);
  }, [channels]);

  const getChannelMessages = useCallback((channelGuid: string): ChatMessage[] => {
    if (!channelMessages[channelGuid]) {
      const channel = channels.find(c => c.guid === channelGuid);
      return getDefaultChannelMessages(channel);
    }
    return channelMessages[channelGuid];
  }, [channelMessages, channels]);

  const addMessage = useCallback((channelGuid: string, message: ChatMessage) => {
    setChannelMessages(prev => {
      const channel = channels.find(c => c.guid === channelGuid);
      const existing = prev[channelGuid] || getDefaultChannelMessages(channel);
      return { ...prev, [channelGuid]: [...existing, message] };
    });
  }, [channels]);

  const createChannelHandler = useCallback(async (data: Omit<Channel, 'id' | 'guid' | 'createdAt'>): Promise<Channel> => {
    try {
      // Create on server
      const newChannel = await channelService.createChannel(data);

      // Optimistically add to state immediately
      setChannels(prev => [...prev, newChannel]);

      // Refresh in background to ensure consistency
      refreshChannels();

      return newChannel;
    } catch (err) {
      console.error('Failed to create channel:', err);
      throw err;
    }
  }, [refreshChannels]);

  const updateChannelHandler = useCallback(async (guid: string, updates: Partial<Channel>): Promise<Channel | null> => {
    try {
      // Optimistically update local state with the known values immediately
      let optimisticChannel: Channel | null = null;
      setChannels(prev => prev.map(c => {
        if (c.guid !== guid) return c;
        optimisticChannel = { ...c, ...updates };
        return optimisticChannel;
      }));

      await channelService.updateChannel(guid, updates);

      // Refresh in background to ensure consistency with server
      refreshChannels();

      return optimisticChannel;
    } catch (err) {
      console.error('Failed to update channel:', err);
      await refreshChannels(); // restore correct state on error
      return null;
    }
  }, [refreshChannels]);

  const deleteChannelHandler = useCallback(async (guid: string): Promise<boolean> => {
    try {
      setChannels(prev => prev.filter(c => c.guid !== guid));
      setChannelMessages(prev => {
        const { [guid]: _, ...rest } = prev;
        return rest;
      });

      await channelService.deleteChannel(guid);

      return true;
    } catch (err) {
      console.error('Failed to delete channel:', err);
      await refreshChannels();
      return false;
    }
  }, [refreshChannels]);

  const setActiveChannel = useCallback((guid: string | null) => {
    setActiveChannelGuid(guid);
  }, []);

  const addAmpToChannelHandler = useCallback(async (channelGuid: string, ampId: string): Promise<Channel | null> => {
    const channel = channels.find(c => c.guid === channelGuid);
    if (!channel) return null;
    
    if (channel.ampIds.includes(ampId)) {
      return channel;
    }

    try {
      const optimisticChannel = {
        ...channel,
        ampIds: [...channel.ampIds, ampId],
      };
      setChannels(prev => prev.map(c => c.guid === channelGuid ? optimisticChannel : c));

     
      await channelService.addAmpToChannel(channel, ampId);

      refreshChannels();

      return optimisticChannel;
    } catch (err) {
      console.error('Failed to add AMP to channel:', err);
   
      await refreshChannels();
      return null;
    }
  }, [channels, refreshChannels]);

  const removeAmpFromChannelHandler = useCallback(async (channelGuid: string, ampId: string): Promise<Channel | null> => {
    const channel = channels.find(c => c.guid === channelGuid);
    if (!channel) return null;

    try {
      const optimisticChannel = {
        ...channel,
        ampIds: channel.ampIds.filter(id => id !== ampId),
      };
      setChannels(prev => prev.map(c => c.guid === channelGuid ? optimisticChannel : c));

      await channelService.removeAmpFromChannel(channel, ampId);

      refreshChannels();

      return optimisticChannel;
    } catch (err) {
      console.error('Failed to remove AMP from channel:', err);

      await refreshChannels();
      return null;
    }
  }, [channels, refreshChannels]);

  const getChannelsForAmpHandler = useCallback((ampId: string): Channel[] => {
    return channels.filter(c => c.ampIds.includes(ampId));
  }, [channels]);

  const value: ChannelContextValue = {
    channels,
    hasChannels,
    loading,
    activeChannelGuid,
    getChannel,
    getChannelById,
    getChannelMessages,
    addMessage,
    createChannel: createChannelHandler,
    updateChannel: updateChannelHandler,
    deleteChannel: deleteChannelHandler,
    setActiveChannel,
    addAmpToChannel: addAmpToChannelHandler,
    removeAmpFromChannel: removeAmpFromChannelHandler,
    getChannelsForAmp: getChannelsForAmpHandler,
    refreshChannels,
  };

  return <ChannelContext.Provider value={value}>{children}</ChannelContext.Provider>;
};
